import {useCallback, useEffect, useMemo} from 'react';
import {mapRtkToDataLoad} from 'store/api';
import type {CollectionState, SaveCollectionFunc} from '../types';
import {
  useCreateSeriesMutation,
  useLazyGetSeriesQuery,
  useUpdateSeriesMutation,
} from '../seriesApi';

/**
 * Hook to handle undefined id.
 */
export const useCollection = (
  id?: string | null,
): {
  collection: CollectionState;
  saveCollection: SaveCollectionFunc;
} => {
  const [getSeries, {data, status, error}] = useLazyGetSeriesQuery();
  const [createSeries] = useCreateSeriesMutation();
  const [updateSeries] = useUpdateSeriesMutation();

  useEffect(() => {
    if (id) {
      getSeries({id});
    }
  }, [id, getSeries]);

  /** Save collection to backend */
  const saveCollection = useCallback<SaveCollectionFunc>(
    collection => {
      return (id ? updateSeries(collection) : createSeries(collection))
        .unwrap()
        .then(updatedCollection => {
          return updatedCollection;
        });
    },
    [createSeries, updateSeries, id],
  );

  return useMemo(() => {
    return {
      collection: mapRtkToDataLoad(status, data, error),
      saveCollection,
    };
  }, [status, data, error, saveCollection]);
};
